import React, { useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";

// Resolve icon name from lucide-react safely
export const Icon = ({ name, className = "w-5 h-5", strokeWidth = 1.8 }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} strokeWidth={strokeWidth} />;
};

// Convert theme object into CSS variables on a wrapper
export const themeStyle = (theme = {}) => ({
  "--tpl-primary": theme.primary || "#6366f1",
  "--tpl-accent": theme.accent || "#f59e0b",
  "--tpl-bg": theme.bg || "#ffffff",
  "--tpl-surface": theme.surface || "#ffffff",
  "--tpl-text": theme.text || "#0f172a",
  "--tpl-muted": theme.muted || "#64748b",
  backgroundColor: "var(--tpl-bg)",
  color: "var(--tpl-text)",
});

// ─── Template Navigation Bar ───────────────────────────────────────────────
export const TemplateNav = ({ template }) => {
  const { siteKey, moduleSlug } = useParams();
  const location = useLocation();
  const activeSlug = moduleSlug || (location.pathname.endsWith("/contact") ? "contact" : "");

  return (
    <header
      data-testid="template-nav"
      className="sticky top-0 z-40 border-b backdrop-blur-md"
      style={{
        backgroundColor: "color-mix(in oklab, var(--tpl-surface) 88%, transparent)",
        borderColor: "color-mix(in oklab, var(--tpl-text) 10%, transparent)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to={`/templates/${template.key}`} className="flex items-center gap-2" data-testid="template-brand">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white" style={{ background: `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))` }}>
            {template.brand.name.charAt(0)}
          </div>
          <div className="leading-tight">
            <p className="font-display font-bold text-sm">{template.brand.name}</p>
            <p className="text-[10px] opacity-70 hidden sm:block">{template.brand.tagline}</p>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-6" data-testid="template-nav-desktop">
          {template.nav.map((n) => {
            const isActive = activeSlug === n.slug;
            return (
              <Link
                key={n.slug}
                to={`/templates/${template.key}/${n.slug}`}
                data-testid={`template-nav-${n.slug}`}
                className="text-sm font-medium transition-colors"
                style={{ color: isActive ? "var(--tpl-primary)" : "var(--tpl-text)", opacity: isActive ? 1 : 0.78 }}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/services"
            className="hidden sm:inline-flex text-xs font-medium px-3 py-2 rounded-full border"
            style={{ borderColor: "color-mix(in oklab, var(--tpl-text) 18%, transparent)" }}
            data-testid="back-to-services"
          >
            ← All Templates
          </Link>
          <Link
            to={`/templates/${template.key}/${template.nav.find(n => n.type === "contact")?.slug || "contact"}`}
            className="text-xs font-semibold px-4 py-2 rounded-full text-white"
            style={{ background: `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))` }}
            data-testid="template-nav-cta"
          >
            Contact
          </Link>
        </div>
      </div>

      {/* Mobile horizontal nav */}
      <div className="md:hidden border-t" style={{ borderColor: "color-mix(in oklab, var(--tpl-text) 8%, transparent)" }}>
        <div className="flex gap-1 overflow-x-auto px-3 py-2 no-scrollbar" data-testid="template-nav-mobile">
          {template.nav.map((n) => {
            const isActive = activeSlug === n.slug;
            return (
              <Link
                key={n.slug}
                to={`/templates/${template.key}/${n.slug}`}
                data-testid={`template-mobile-nav-${n.slug}`}
                className="whitespace-nowrap text-xs px-3 py-1.5 rounded-full"
                style={{
                  background: isActive ? "color-mix(in oklab, var(--tpl-primary) 14%, transparent)" : "transparent",
                  color: isActive ? "var(--tpl-primary)" : "var(--tpl-text)",
                  border: `1px solid color-mix(in oklab, var(--tpl-text) ${isActive ? 0 : 12}%, transparent)`,
                }}
              >
                {n.label}
              </Link>
            );
          })}
        </div>
      </div>
    </header>
  );
};

// ─── Generic section wrapper for consistent spacing ────────────────────────
export const SectionShell = ({ id, title, subtitle, eyebrow, children, dataTestid }) => (
  <section id={id} data-testid={dataTestid || `section-${id}`} className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl">
        {eyebrow && (
          <p className="text-xs uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: "var(--tpl-primary)" }}>
            {eyebrow}
          </p>
        )}
        {title && (
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {title}
          </h2>
        )}
        {subtitle && <p className="mt-3 text-base opacity-75 max-w-xl">{subtitle}</p>}
      </div>
      <div className="mt-10 sm:mt-14">{children}</div>
    </div>
  </section>
);

// ─── HERO ──────────────────────────────────────────────────────────────────
export const Hero = ({ template, hero }) => (
  <section
    data-testid="template-hero"
    className="relative overflow-hidden"
    style={{
      background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 12%, var(--tpl-bg)) 0%, var(--tpl-bg) 65%)`,
    }}
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 lg:py-32 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        {hero.eyebrow && (
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.2em] font-semibold"
            style={{
              backgroundColor: "color-mix(in oklab, var(--tpl-primary) 14%, transparent)",
              color: "var(--tpl-primary)",
            }}
          >
            ● {hero.eyebrow}
          </span>
        )}
        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
          {hero.title}
        </h1>
        <p className="mt-5 text-base sm:text-lg opacity-80 max-w-xl">{hero.subtitle}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={`/templates/${template.key}/${hero.primaryHref}`}
            className="px-6 py-3 rounded-full text-white font-semibold shadow-lg transition-transform hover:-translate-y-0.5 inline-flex items-center gap-2"
            style={{ background: `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))` }}
            data-testid="hero-primary-cta"
          >
            {hero.primaryCta} <Icons.ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/services"
            className="px-6 py-3 rounded-full font-medium border inline-flex items-center gap-2"
            style={{ borderColor: "color-mix(in oklab, var(--tpl-text) 18%, transparent)" }}
            data-testid="hero-secondary-cta"
          >
            <Icons.LayoutGrid className="w-4 h-4" /> View all templates
          </Link>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="relative"
      >
        <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl"
          style={{ boxShadow: `0 30px 80px color-mix(in oklab, var(--tpl-primary) 35%, transparent)` }}>
          <img src={hero.image} alt={template.brand.name} className="w-full h-full object-cover" />
        </div>
        <div
          className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl p-4 shadow-xl"
          style={{ backgroundColor: "var(--tpl-surface)", border: "1px solid color-mix(in oklab, var(--tpl-text) 10%, transparent)" }}
        >
          <p className="text-xs uppercase tracking-widest opacity-60">{template.industry}</p>
          <p className="font-display font-bold text-lg" style={{ color: "var(--tpl-primary)" }}>{template.brand.name}</p>
        </div>
      </motion.div>
    </div>
  </section>
);

// ─── ABOUT ─────────────────────────────────────────────────────────────────
export const About = ({ data, dataTestid = "about-section" }) => (
  <SectionShell title={data.title} subtitle={data.subtitle} eyebrow="About us" dataTestid={dataTestid}>
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
        <p className="text-base leading-relaxed opacity-85">{data.body}</p>
        <ul className="mt-6 space-y-3">
          {data.points?.map((p, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: "color-mix(in oklab, var(--tpl-primary) 16%, transparent)", color: "var(--tpl-primary)" }}>
                <Icons.Check className="w-3.5 h-3.5" />
              </span>
              <span className="text-sm opacity-90">{p}</span>
            </li>
          ))}
        </ul>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl">
        <img src={data.image} alt={data.title} className="w-full h-full object-cover" />
      </motion.div>
    </div>
  </SectionShell>
);

// ─── ITEM GRID (Services / Products / Collections / Packages / Properties / Features / Academics / Departments) ─
export const ItemGrid = ({ data, variant = "feature", dataTestid }) => {
  // variants: feature (icon cards), product (image cards w/ price), package (image+price+desc)
  const cards = data.items || [];
  const hasImage = cards.some((c) => c.image);
  const hasPrice = cards.some((c) => c.price);
  const isImageGrid = hasImage || variant === "product";

  return (
    <SectionShell title={data.title} subtitle={data.subtitle} eyebrow={variant === "product" ? "Catalog" : "What we offer"} dataTestid={dataTestid}>
      <div className={`grid gap-5 ${isImageGrid ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
        {cards.map((it, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.45, delay: i * 0.05 }}
            data-testid={`item-card-${i}`}
            className="group rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all hover:-translate-y-1 border"
            style={{
              backgroundColor: "var(--tpl-surface)",
              borderColor: "color-mix(in oklab, var(--tpl-text) 9%, transparent)",
            }}
          >
            {it.image && (
              <div className="aspect-[4/3] overflow-hidden">
                <img src={it.image} alt={it.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            )}
            <div className="p-5">
              {it.icon && !it.image && (
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 18%, transparent), color-mix(in oklab, var(--tpl-accent) 12%, transparent))`,
                    color: "var(--tpl-primary)",
                  }}
                >
                  <Icon name={it.icon} className="w-5 h-5" />
                </div>
              )}
              <h3 className="font-display text-lg font-bold leading-snug">{it.title}</h3>
              {it.desc && <p className="mt-2 text-sm opacity-75 leading-relaxed">{it.desc}</p>}
              {it.price && (
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-bold text-base" style={{ color: "var(--tpl-primary)" }}>{it.price}</span>
                  <button
                    className="text-xs font-semibold px-3 py-1.5 rounded-full"
                    style={{ background: `color-mix(in oklab, var(--tpl-primary) 12%, transparent)`, color: "var(--tpl-primary)" }}
                    data-testid={`item-cta-${i}`}
                  >
                    {variant === "package" ? "View details" : "Enquire"}
                  </button>
                </div>
              )}
            </div>
          </motion.article>
        ))}
      </div>
    </SectionShell>
  );
};

// ─── DOCTORS / TRAINERS / TEAM ─────────────────────────────────────────────
export const People = ({ data, dataTestid }) => (
  <SectionShell title={data.title} subtitle={data.subtitle} eyebrow="Our experts" dataTestid={dataTestid}>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {data.items.map((p, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: i * 0.06 }}
          data-testid={`person-card-${i}`}
          className="rounded-2xl overflow-hidden border"
          style={{
            backgroundColor: "var(--tpl-surface)",
            borderColor: "color-mix(in oklab, var(--tpl-text) 10%, transparent)",
          }}
        >
          <div className="aspect-square overflow-hidden">
            <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
          </div>
          <div className="p-5">
            <p className="font-display font-bold text-lg">{p.name}</p>
            <p className="text-sm font-medium" style={{ color: "var(--tpl-primary)" }}>{p.role}</p>
            {p.desc && <p className="mt-2 text-sm opacity-75">{p.desc}</p>}
          </div>
        </motion.div>
      ))}
    </div>
  </SectionShell>
);

// ─── GALLERY / LOOKBOOK / PORTFOLIO / PHOTO VIEW ───────────────────────────
export const Gallery = ({ data, dataTestid }) => (
  <SectionShell title={data.title} subtitle={data.subtitle} eyebrow="Gallery" dataTestid={dataTestid}>
    <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
      {data.images.map((src, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}
          className="mb-4 break-inside-avoid rounded-2xl overflow-hidden group cursor-zoom-in"
          data-testid={`gallery-image-${i}`}
        >
          <img src={src} alt={`Gallery ${i + 1}`} loading="lazy" className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700" />
        </motion.div>
      ))}
    </div>
  </SectionShell>
);

// ─── REVIEWS / TESTIMONIALS ────────────────────────────────────────────────
export const Reviews = ({ data, dataTestid }) => (
  <SectionShell title={data.title} subtitle={data.subtitle} eyebrow="What people say" dataTestid={dataTestid}>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {data.items.map((r, i) => (
        <motion.figure
          key={i}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: i * 0.06 }}
          data-testid={`review-card-${i}`}
          className="rounded-2xl p-6 border flex flex-col"
          style={{
            backgroundColor: "var(--tpl-surface)",
            borderColor: "color-mix(in oklab, var(--tpl-text) 9%, transparent)",
          }}
        >
          <div className="flex gap-0.5 mb-3" style={{ color: "var(--tpl-accent)" }}>
            {Array.from({ length: r.rating || 5 }).map((_, idx) => (
              <Icons.Star key={idx} className="w-4 h-4 fill-current" />
            ))}
          </div>
          <blockquote className="text-sm leading-relaxed flex-1">“{r.text}”</blockquote>
          <figcaption className="mt-5 flex items-center gap-3">
            <img src={r.avatar} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
            <div>
              <p className="text-sm font-semibold">{r.name}</p>
              <p className="text-xs opacity-65">{r.role}</p>
            </div>
          </figcaption>
        </motion.figure>
      ))}
    </div>
  </SectionShell>
);

// ─── PRICING ───────────────────────────────────────────────────────────────
export const Pricing = ({ data, dataTestid }) => (
  <SectionShell title={data.title} subtitle={data.subtitle} eyebrow="Pricing" dataTestid={dataTestid}>
    <div className="grid md:grid-cols-3 gap-5">
      {data.plans.map((p, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: i * 0.08 }}
          data-testid={`pricing-plan-${i}`}
          className={`rounded-3xl p-7 border ${p.highlight ? "scale-[1.02] shadow-2xl" : "shadow-sm"}`}
          style={{
            backgroundColor: p.highlight ? "color-mix(in oklab, var(--tpl-primary) 8%, var(--tpl-surface))" : "var(--tpl-surface)",
            borderColor: p.highlight ? "var(--tpl-primary)" : "color-mix(in oklab, var(--tpl-text) 12%, transparent)",
          }}
        >
          {p.highlight && (
            <span className="inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full mb-3" style={{ background: "var(--tpl-primary)", color: "#fff" }}>
              Most Popular
            </span>
          )}
          <p className="font-display font-bold text-xl">{p.name}</p>
          <p className="text-sm opacity-70 mt-1">{p.desc}</p>
          <div className="mt-5 flex items-end gap-1">
            <span className="font-display font-bold text-4xl" style={{ color: "var(--tpl-primary)" }}>{p.price}</span>
            <span className="text-sm opacity-65 mb-1">{p.period}</span>
          </div>
          <ul className="mt-6 space-y-2.5">
            {p.features.map((f, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <Icons.Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
                <span className="opacity-85">{f}</span>
              </li>
            ))}
          </ul>
          <button
            className="mt-6 w-full px-5 py-3 rounded-full font-semibold text-sm"
            style={{
              background: p.highlight ? `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))` : "transparent",
              border: p.highlight ? "none" : `1px solid color-mix(in oklab, var(--tpl-text) 18%, transparent)`,
              color: p.highlight ? "#fff" : "var(--tpl-text)",
            }}
            data-testid={`pricing-cta-${i}`}
          >
            {p.cta}
          </button>
        </motion.div>
      ))}
    </div>
  </SectionShell>
);

// ─── FAQ ───────────────────────────────────────────────────────────────────
export const Faq = ({ data, dataTestid }) => {
  const [open, setOpen] = React.useState(-1);
  return (
    <SectionShell title={data.title} subtitle={data.subtitle} eyebrow="FAQ" dataTestid={dataTestid}>
      <div className="max-w-3xl mx-auto space-y-3">
        {data.items.map((f, i) => (
          <div
            key={i}
            className="rounded-2xl border overflow-hidden"
            style={{
              backgroundColor: "var(--tpl-surface)",
              borderColor: "color-mix(in oklab, var(--tpl-text) 10%, transparent)",
            }}
            data-testid={`faq-item-${i}`}
          >
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 p-5 text-left"
              data-testid={`faq-toggle-${i}`}
            >
              <span className="font-semibold text-sm sm:text-base">{f.q}</span>
              <Icons.ChevronDown className={`w-5 h-5 transition-transform ${open === i ? "rotate-180" : ""}`} style={{ color: "var(--tpl-primary)" }} />
            </button>
            {open === i && (
              <div className="px-5 pb-5 text-sm opacity-80 leading-relaxed" data-testid={`faq-answer-${i}`}>
                {f.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </SectionShell>
  );
};

// ─── CONTACT / ENQUIRY / APPOINTMENT / BOOKING / ORDER / ADMISSION / NEWSLETTER ─
const FIELD_LABELS = {
  name: "Full name",
  email: "Email address",
  phone: "Phone number",
  address: "Delivery address",
  date: "Preferred date",
  message: "Message",
  matter: "Nature of legal matter",
  property: "Property of interest",
  program: "Program / course",
  grade: "Class / grade",
  vehicle: "Vehicle make & model",
  department: "Department",
  destination: "Destination",
  company: "Company",
};

export const Contact = ({ data, dataTestid, template }) => {
  const [form, setForm] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const fields = data.fields || ["name", "email", "phone", "message"];

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((er) => ({ ...er, [e.target.name]: "" }));
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const normalisePhone = (raw) => (raw || "").replace(/\D+/g, "").replace(/^91/, "").replace(/^0/, "");
  const isValidPhone = (raw) => /^[6-9]\d{9}$/.test(normalisePhone(raw));

  const validate = () => {
    const required = fields.filter((f) => f !== "message");
    const er = {};
    required.forEach((f) => {
      const val = (form[f] || "").trim();
      if (!val) er[f] = "This field is required.";
      else if (f === "email" && !EMAIL_RE.test(val)) er[f] = "Enter a valid email.";
      else if (f === "phone" && !isValidPhone(val)) er[f] = "Enter a valid 10-digit Indian mobile.";
      else if (f === "name" && val.length < 2) er[f] = "Please enter your full name.";
    });
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  // Build a WhatsApp link from the template's contact phone (fallback to platform number)
  const PLATFORM_WA = "919141302057";
  const targetNumber = React.useMemo(() => {
    const tplPhone = template?.modules?.contact?.phone || template?.modules?.[Object.keys(template?.modules || {}).find?.(k => template?.modules?.[k]?.phone)]?.phone;
    const fromPhone = (tplPhone || data.phone || "").toString().replace(/\D+/g, "");
    if (/^91\d{10}$/.test(fromPhone)) return fromPhone;
    if (/^\d{10}$/.test(fromPhone)) return `91${fromPhone}`;
    return PLATFORM_WA;
  }, [template, data.phone]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const cleanPhone = form.phone ? `+91 ${normalisePhone(form.phone)}` : "";
    const lines = [
      `Namaste 🙏 I'm enquiring via the ${template?.brand?.name || "your"} website.`,
      `*Section:* ${data.title || "Contact"}`,
      "",
    ];
    fields.forEach((f) => {
      const val = (form[f] || "").trim();
      if (!val) return;
      const label = FIELD_LABELS[f] || f;
      lines.push(`*${label}:* ${f === "phone" ? cleanPhone : val}`);
    });
    const text = lines.join("\n");
    const url = `https://wa.me/${targetNumber}?text=${encodeURIComponent(text)}`;

    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const inputBorder = (f) => `color-mix(in oklab, ${errors[f] ? "#f43f5e" : "var(--tpl-text)"} ${errors[f] ? "60%" : "14%"}, transparent)`;

  return (
    <SectionShell title={data.title} subtitle={data.subtitle} eyebrow="Get in touch" dataTestid={dataTestid}>
      <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
        <div className="lg:col-span-2 space-y-5">
          {data.address && (
            <div className="flex gap-3" data-testid="contact-address">
              <Icons.MapPin className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
              <div>
                <p className="text-xs uppercase tracking-widest opacity-60">Address</p>
                <p className="text-sm font-medium">{data.address}</p>
              </div>
            </div>
          )}
          {data.phone && (
            <div className="flex gap-3" data-testid="contact-phone">
              <Icons.Phone className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
              <div>
                <p className="text-xs uppercase tracking-widest opacity-60">Phone</p>
                <p className="text-sm font-medium">{data.phone}</p>
              </div>
            </div>
          )}
          {data.email && (
            <div className="flex gap-3" data-testid="contact-email">
              <Icons.Mail className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
              <div>
                <p className="text-xs uppercase tracking-widest opacity-60">Email</p>
                <p className="text-sm font-medium">{data.email}</p>
              </div>
            </div>
          )}
          {data.hours && (
            <div className="flex gap-3" data-testid="contact-hours">
              <Icons.Clock className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
              <div>
                <p className="text-xs uppercase tracking-widest opacity-60">Hours</p>
                <p className="text-sm font-medium">{data.hours}</p>
              </div>
            </div>
          )}
          {data.note && (
            <p className="text-xs p-3 rounded-xl" style={{ backgroundColor: "color-mix(in oklab, var(--tpl-primary) 10%, transparent)", color: "var(--tpl-primary)" }}>
              {data.note}
            </p>
          )}
        </div>

        <form
          onSubmit={onSubmit}
          noValidate
          data-testid="contact-form"
          className="lg:col-span-3 rounded-3xl p-6 sm:p-8 border"
          style={{
            backgroundColor: "var(--tpl-surface)",
            borderColor: "color-mix(in oklab, var(--tpl-text) 10%, transparent)",
          }}
        >
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => {
              const isTextarea = f === "message";
              const isDate = f === "date";
              return (
                <div key={f} className={isTextarea ? "sm:col-span-2" : ""}>
                  <label className="text-xs uppercase tracking-widest opacity-60">{FIELD_LABELS[f] || f}</label>
                  {isTextarea ? (
                    <textarea
                      name={f}
                      value={form[f] || ""}
                      onChange={onChange}
                      rows={4}
                      data-testid={`contact-input-${f}`}
                      className="mt-1.5 w-full bg-transparent border rounded-xl px-4 py-3 text-sm outline-none transition-colors resize-none"
                      style={{ borderColor: inputBorder(f) }}
                    />
                  ) : (
                    <input
                      name={f}
                      type={isDate ? "date" : (f === "email" ? "email" : "text")}
                      value={form[f] || ""}
                      onChange={onChange}
                      data-testid={`contact-input-${f}`}
                      placeholder={f === "phone" ? "10-digit mobile" : undefined}
                      className="mt-1.5 w-full bg-transparent border rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                      style={{ borderColor: inputBorder(f) }}
                    />
                  )}
                  {errors[f] && (
                    <p data-testid={`contact-error-${f}`} className="mt-1 text-xs text-rose-500">{errors[f]}</p>
                  )}
                </div>
              );
            })}
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            className="mt-6 w-full px-6 py-3 rounded-full font-semibold text-white shadow-lg inline-flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))` }}
          >
            <Icons.MessageCircle className="w-4 h-4" />
            Submit &amp; Continue on WhatsApp
          </button>
        </form>
      </div>
    </SectionShell>
  );
};

// ─── FOOTER ────────────────────────────────────────────────────────────────
export const TemplateFooter = ({ template }) => {
  // Find any module with concrete contact info (phone/email/hours/address)
  const contactInfo = React.useMemo(() => {
    const modules = template.modules || {};
    for (const key of Object.keys(modules)) {
      const m = modules[key];
      if (m && (m.phone || m.email || m.address || m.hours)) return m;
    }
    return {};
  }, [template]);

  return (
    <footer
      data-testid="template-footer"
      className="mt-10 border-t"
      style={{
        backgroundColor: "color-mix(in oklab, var(--tpl-text) 4%, var(--tpl-surface))",
        borderColor: "color-mix(in oklab, var(--tpl-text) 10%, transparent)",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid sm:grid-cols-2 md:grid-cols-4 gap-8">
        <div className="md:col-span-1">
          <div className="flex items-center gap-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white"
              style={{ background: `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))` }}
            >
              {template.brand.name.charAt(0)}
            </div>
            <div className="leading-tight">
              <p className="font-display font-bold text-base" style={{ color: "var(--tpl-primary)" }}>
                {template.brand.name}
              </p>
              <p className="text-[11px] opacity-65">{template.brand.tagline}</p>
            </div>
          </div>
          <p className="text-xs opacity-70 mt-4 leading-relaxed">
            {template.summary}
          </p>
          <div className="mt-5 flex gap-2">
            {[
              { i: "Facebook", l: "Facebook" },
              { i: "Instagram", l: "Instagram" },
              { i: "Linkedin", l: "LinkedIn" },
              { i: "Youtube", l: "YouTube" },
            ].map(({ i, l }) => {
              const Cmp = Icons[i] || Icons.Globe;
              return (
                <a
                  key={i}
                  href="#"
                  aria-label={l}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                  style={{
                    backgroundColor: "color-mix(in oklab, var(--tpl-primary) 10%, transparent)",
                    color: "var(--tpl-primary)",
                  }}
                  data-testid={`footer-social-${i.toLowerCase()}`}
                >
                  <Cmp className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest opacity-60 mb-3 font-semibold">Quick Links</p>
          <ul className="space-y-2">
            {template.nav.slice(0, 6).map((n) => (
              <li key={n.slug}>
                <Link
                  to={`/templates/${template.key}/${n.slug}`}
                  className="text-sm opacity-80 hover:opacity-100 inline-flex items-center gap-2"
                >
                  <Icons.ChevronRight className="w-3.5 h-3.5" style={{ color: "var(--tpl-primary)" }} />
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest opacity-60 mb-3 font-semibold">Contact</p>
          <ul className="space-y-3 text-sm">
            {contactInfo.address && (
              <li className="flex items-start gap-2" data-testid="footer-address">
                <Icons.MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
                <span className="opacity-85">{contactInfo.address}</span>
              </li>
            )}
            {contactInfo.phone && (
              <li className="flex items-start gap-2" data-testid="footer-phone">
                <Icons.Phone className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
                <a href={`tel:${contactInfo.phone.replace(/\s+/g, "")}`} className="opacity-85 hover:opacity-100">
                  {contactInfo.phone}
                </a>
              </li>
            )}
            {contactInfo.email && (
              <li className="flex items-start gap-2" data-testid="footer-email">
                <Icons.Mail className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
                <a href={`mailto:${contactInfo.email}`} className="opacity-85 hover:opacity-100">
                  {contactInfo.email}
                </a>
              </li>
            )}
            {contactInfo.hours && (
              <li className="flex items-start gap-2" data-testid="footer-hours">
                <Icons.Clock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--tpl-primary)" }} />
                <span className="opacity-85">{contactInfo.hours}</span>
              </li>
            )}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-widest opacity-60 mb-3 font-semibold">About</p>
          <p className="text-xs opacity-75 leading-relaxed">{template.industry}</p>
          <p className="text-xs opacity-70 mt-2 leading-relaxed">
            Demo template — content is illustrative.
          </p>
          <Link
            to="/services"
            className="mt-4 text-sm font-semibold inline-flex items-center gap-2"
            style={{ color: "var(--tpl-primary)" }}
            data-testid="footer-back-to-services"
          >
            All Templates <Icons.ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div
        className="border-t"
        style={{ borderColor: "color-mix(in oklab, var(--tpl-text) 8%, transparent)" }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs opacity-70">
          <p>© {new Date().getFullYear()} {template.brand.name}. All rights reserved.</p>
          <p>Crafted by <Link to="/" className="font-semibold underline" style={{ color: "var(--tpl-primary)" }}>Digital Onboarding</Link></p>
        </div>
      </div>
    </footer>
  );
};

// ─── STATS STRIP (trust indicators) ───────────────────────────────────────
const STATS_BY_INDUSTRY = {
  Healthcare: [
    { k: "Patients Treated", v: "2L+", icon: "HeartPulse" },
    { k: "Expert Doctors", v: "250+", icon: "Stethoscope" },
    { k: "Years of Care", v: "30+", icon: "Award" },
    { k: "Success Rate", v: "99%", icon: "ShieldCheck" },
  ],
  Dentistry: [
    { k: "Smiles Delivered", v: "25K+", icon: "Smile" },
    { k: "Specialists", v: "12+", icon: "Stethoscope" },
    { k: "Years Practising", v: "18+", icon: "Award" },
    { k: "Rating", v: "4.9★", icon: "Star" },
  ],
  Wellness: [
    { k: "Clients Pampered", v: "30K+", icon: "Heart" },
    { k: "Certified Therapists", v: "20+", icon: "Sparkles" },
    { k: "Years of Service", v: "12+", icon: "Award" },
    { k: "Repeat Visits", v: "82%", icon: "Repeat" },
  ],
  Beauty: [
    { k: "Happy Customers", v: "50K+", icon: "Heart" },
    { k: "Product SKUs", v: "200+", icon: "Package" },
    { k: "Cities Shipped", v: "180+", icon: "MapPin" },
    { k: "Rating", v: "4.8★", icon: "Star" },
  ],
  Fitness: [
    { k: "Active Members", v: "1,500+", icon: "Users" },
    { k: "Certified Trainers", v: "18", icon: "Dumbbell" },
    { k: "Open Hours", v: "24/7", icon: "Clock" },
    { k: "Avg. Member Rating", v: "4.9★", icon: "Star" },
  ],
  "K-12 Education": [
    { k: "Students", v: "2,400+", icon: "Users" },
    { k: "Faculty", v: "140+", icon: "GraduationCap" },
    { k: "Years", v: "34+", icon: "Award" },
    { k: "Class 12 Pass", v: "100%", icon: "Trophy" },
  ],
  "Higher Education": [
    { k: "Students", v: "5,200+", icon: "Users" },
    { k: "Faculty & PhDs", v: "280+", icon: "GraduationCap" },
    { k: "Placement", v: "92%", icon: "Briefcase" },
    { k: "NIRF Rank", v: "Top 50", icon: "Trophy" },
  ],
  Hospitality: [
    { k: "Happy Guests", v: "6,000+", icon: "Smile" },
    { k: "Years Hosting", v: "9+", icon: "Award" },
    { k: "Avg. Rating", v: "4.9★", icon: "Star" },
    { k: "Repeat Stays", v: "65%", icon: "Repeat" },
  ],
  Travel: [
    { k: "Travellers Served", v: "12K+", icon: "Plane" },
    { k: "Curated Packages", v: "120+", icon: "Map" },
    { k: "Countries", v: "45+", icon: "Globe" },
    { k: "Rating", v: "4.9★", icon: "Star" },
  ],
  "Food Service": [
    { k: "Orders Delivered", v: "1L+", icon: "ShoppingBag" },
    { k: "Avg. Delivery", v: "32 min", icon: "Clock" },
    { k: "Cities", v: "12+", icon: "MapPin" },
    { k: "Rating", v: "4.8★", icon: "Star" },
  ],
  "F&B": [
    { k: "Orders Served", v: "1L+", icon: "ShoppingBag" },
    { k: "Outlets", v: "12", icon: "MapPin" },
    { k: "Years", v: "8+", icon: "Award" },
    { k: "Rating", v: "4.8★", icon: "Star" },
  ],
  "Gourmet Food": [
    { k: "Customers", v: "20K+", icon: "Users" },
    { k: "Cities Shipped", v: "200+", icon: "MapPin" },
    { k: "Direct Farms", v: "12", icon: "Sprout" },
    { k: "Avg. Rating", v: "4.9★", icon: "Star" },
  ],
  "Retail Grocery": [
    { k: "Households Served", v: "10K+/day", icon: "Home" },
    { k: "SKUs", v: "5,000+", icon: "Package" },
    { k: "Avg. Delivery", v: "30 min", icon: "Clock" },
    { k: "Rating", v: "4.7★", icon: "Star" },
  ],
  Apparel: [
    { k: "Designs", v: "1,200+", icon: "Sparkles" },
    { k: "Master Artisans", v: "40+", icon: "Award" },
    { k: "Cities Shipped", v: "300+", icon: "MapPin" },
    { k: "Rating", v: "4.9★", icon: "Star" },
  ],
  "Home & Living": [
    { k: "Pieces Delivered", v: "30K+", icon: "Package" },
    { k: "Master Craftsmen", v: "55+", icon: "Award" },
    { k: "Years", v: "20+", icon: "Trophy" },
    { k: "Warranty", v: "10 yrs", icon: "ShieldCheck" },
  ],
  "Real Estate": [
    { k: "Homes Sold", v: "4,500+", icon: "Home" },
    { k: "Cities", v: "8", icon: "MapPin" },
    { k: "Years", v: "22+", icon: "Award" },
    { k: "Repeat Clients", v: "38%", icon: "Repeat" },
  ],
  Legal: [
    { k: "Cases Handled", v: "1,200+", icon: "Scale" },
    { k: "Practice Areas", v: "8", icon: "BookOpen" },
    { k: "Years", v: "27+", icon: "Award" },
    { k: "Win Rate", v: "94%", icon: "Trophy" },
  ],
  Automotive: [
    { k: "Vehicles Treated", v: "8,000+", icon: "Car" },
    { k: "Warranty", v: "10 yrs", icon: "ShieldCheck" },
    { k: "Master Detailers", v: "12", icon: "Wrench" },
    { k: "Rating", v: "4.9★", icon: "Star" },
  ],
  "SaaS / Marketing": [
    { k: "Brands Powered", v: "800+", icon: "Briefcase" },
    { k: "Emails / Month", v: "120M+", icon: "Mail" },
    { k: "Avg. ROI", v: "11×", icon: "TrendingUp" },
    { k: "Uptime", v: "99.98%", icon: "ShieldCheck" },
  ],
};

const STATS_DEFAULT = [
  { k: "Happy Customers", v: "10K+", icon: "Users" },
  { k: "Years of Service", v: "15+", icon: "Award" },
  { k: "Cities Served", v: "50+", icon: "MapPin" },
  { k: "Rating", v: "4.9★", icon: "Star" },
];

export const StatsStrip = ({ template, dataTestid = "section-stats" }) => {
  const stats = STATS_BY_INDUSTRY[template.industry] || STATS_DEFAULT;
  return (
    <section className="py-12 sm:py-16" data-testid={dataTestid}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 rounded-3xl p-6 sm:p-10 border"
          style={{
            background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 8%, var(--tpl-surface)), var(--tpl-surface))`,
            borderColor: "color-mix(in oklab, var(--tpl-primary) 18%, transparent)",
          }}
        >
          {stats.map((s, i) => {
            const Cmp = Icons[s.icon] || Icons.Sparkles;
            return (
              <div key={i} className="text-center sm:text-left" data-testid={`stat-${i}`}>
                <div
                  className="w-10 h-10 rounded-xl mx-auto sm:mx-0 mb-2 flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 18%, transparent), color-mix(in oklab, var(--tpl-accent) 14%, transparent))`,
                    color: "var(--tpl-primary)",
                  }}
                >
                  <Cmp className="w-5 h-5" />
                </div>
                <p className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "var(--tpl-primary)" }}>{s.v}</p>
                <p className="text-xs sm:text-sm opacity-70 mt-0.5">{s.k}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── WHY CHOOSE US ────────────────────────────────────────────────────────
const WHY_BY_INDUSTRY = {
  Healthcare: [
    { title: "Board-Certified Specialists", icon: "Stethoscope", desc: "Doctors from India's top medical institutions, with decades of experience." },
    { title: "Modern Diagnostics", icon: "ScanLine", desc: "3T MRI, 128-slice CT and digital pathology — fast, accurate results." },
    { title: "24×7 Emergency", icon: "Ambulance", desc: "Always-on trauma & critical care with NABH-grade ICU." },
    { title: "Transparent Pricing", icon: "ReceiptText", desc: "Cashless insurance on all major TPAs; no hidden charges." },
  ],
  Dentistry: [
    { title: "Painless Procedures", icon: "Smile", desc: "Latest sedation & rotary endo for a stress-free chair time." },
    { title: "World-class Sterilisation", icon: "ShieldCheck", desc: "Class B autoclaves & disposable kits for every patient." },
    { title: "Latest Tech", icon: "Sparkles", desc: "Digital scanners, Invisalign certified clinic." },
    { title: "Family Friendly", icon: "Users", desc: "Kid-friendly chairs and gentle paediatric care." },
  ],
  Wellness: [
    { title: "Authentic Ayurveda", icon: "Leaf", desc: "Therapies designed by Kerala-trained Vaidyas." },
    { title: "Trained Therapists", icon: "HeartHandshake", desc: "Certified, professional and warm — every session." },
    { title: "Calm Spaces", icon: "Flower", desc: "Sound-proof rooms, natural light, aromatherapy." },
    { title: "Personalised Plans", icon: "ClipboardList", desc: "Tailored to your dosha, lifestyle and goals." },
  ],
  Beauty: [
    { title: "Dermat-Tested", icon: "ShieldCheck", desc: "Every product verified by Indian dermatologists." },
    { title: "Cruelty-Free", icon: "Heart", desc: "Vegan formulations, no animal testing." },
    { title: "Made in India", icon: "Sparkles", desc: "Manufactured in our own GMP-certified facility." },
    { title: "Real Reviews", icon: "Star", desc: "Verified-purchase ratings from 50K+ shoppers." },
  ],
  Fitness: [
    { title: "Olympic-Grade Equipment", icon: "Dumbbell", desc: "Hammer Strength, Eleiko bars, Cybex cardio." },
    { title: "Certified Coaches", icon: "Award", desc: "IPF, ACE & NASM certified trainers." },
    { title: "Open 24/7", icon: "Clock", desc: "Train on your schedule, with full safety." },
    { title: "Diet + Recovery", icon: "Salad", desc: "Custom meal plans and recovery sessions included." },
  ],
  "K-12 Education": [
    { title: "Holistic Curriculum", icon: "BookOpen", desc: "CBSE + life skills, music, sports & coding." },
    { title: "Best-in-class Faculty", icon: "GraduationCap", desc: "1:18 student-to-teacher ratio, post-graduate teachers." },
    { title: "Safe & Secure", icon: "ShieldCheck", desc: "CCTV, GPS-tracked transport, female attendants." },
    { title: "Top Placements", icon: "Trophy", desc: "Alumni in IITs, NITs and global universities." },
  ],
  "Higher Education": [
    { title: "Industry-Aligned", icon: "Briefcase", desc: "Curriculum reviewed by Google, ISRO, TCS, Microsoft." },
    { title: "Research-First", icon: "FlaskConical", desc: "120+ publications and 8 active research labs." },
    { title: "Top Placements", icon: "Trophy", desc: "92% placement; ₹52L highest, ₹8.5L average." },
    { title: "Global Exchange", icon: "Globe", desc: "Tie-ups with 14 international universities." },
  ],
  Hospitality: [
    { title: "Hand-picked Locations", icon: "MapPin", desc: "Hidden gems with breath-taking views." },
    { title: "Warm Hospitality", icon: "HeartHandshake", desc: "Family-run, owner-hosted, never staffy." },
    { title: "Local Experiences", icon: "Compass", desc: "Curated walks, food tours and adventures." },
    { title: "Best Price Guarantee", icon: "BadgePercent", desc: "Direct rates always cheaper than OTAs." },
  ],
  Travel: [
    { title: "Designer Itineraries", icon: "Map", desc: "Hand-crafted by certified travel designers." },
    { title: "Zero Hidden Costs", icon: "ReceiptText", desc: "Inclusive pricing — no last-minute surprises." },
    { title: "24×7 On-Trip Support", icon: "Headphones", desc: "Local representatives in every destination." },
    { title: "Verified Stays", icon: "ShieldCheck", desc: "Hand-inspected hotels and homestays." },
  ],
  "Food Service": [
    { title: "Hot in 30 Mins", icon: "Clock", desc: "From kitchen to your door, piping hot." },
    { title: "Home-style Recipes", icon: "ChefHat", desc: "Cooked by mothers, the way you remember." },
    { title: "Hygienic & FSSAI Cert.", icon: "ShieldCheck", desc: "Open-kitchen policy and FSSAI-licensed." },
    { title: "Affordable Plans", icon: "BadgePercent", desc: "Monthly tiffin plans starting ₹4,999." },
  ],
  "F&B": [
    { title: "Made Fresh Daily", icon: "ChefHat", desc: "Hand-churned every morning, never frozen." },
    { title: "Real Ingredients", icon: "Sprout", desc: "No artificial colours or preservatives." },
    { title: "32 Flavours", icon: "Sparkles", desc: "From classic to seasonal sorbets." },
    { title: "Loved by Families", icon: "Heart", desc: "Kid-friendly and birthday-party ready." },
  ],
  "Gourmet Food": [
    { title: "Sourced Directly", icon: "Sprout", desc: "From Kashmir, Iran and California — no middlemen." },
    { title: "Hand-graded", icon: "ShieldCheck", desc: "Visual & taste-tested by our in-house team." },
    { title: "Vacuum-fresh Packed", icon: "Package", desc: "Sealed within 48 hours of grading." },
    { title: "Bulk & Gifting", icon: "Gift", desc: "Festive hampers and corporate gifting solutions." },
  ],
  "Retail Grocery": [
    { title: "Daily Fresh", icon: "Sprout", desc: "Fruits & veggies arrive fresh every morning." },
    { title: "Big Brand SKUs", icon: "Package", desc: "5,000+ trusted brands under one roof." },
    { title: "Best Prices", icon: "BadgePercent", desc: "Local rates with home delivery, always." },
    { title: "30-Minute Delivery", icon: "Clock", desc: "Free delivery on orders above ₹499." },
  ],
  Apparel: [
    { title: "Master Craftsmanship", icon: "Scissors", desc: "Made by 40+ master artisans across India." },
    { title: "Hand-curated Designers", icon: "Sparkles", desc: "Emerging Indian designers, exclusive to us." },
    { title: "Easy Exchanges", icon: "Repeat", desc: "Free 7-day exchanges, no questions asked." },
    { title: "Try Before You Buy", icon: "ShoppingBag", desc: "In-home trial for orders above ₹10,000." },
  ],
  "Home & Living": [
    { title: "Solid Wood Only", icon: "TreePine", desc: "100% teak, sheesham & reclaimed wood." },
    { title: "10-Year Warranty", icon: "ShieldCheck", desc: "Workmanship & materials covered." },
    { title: "Custom Sizing", icon: "Ruler", desc: "Bespoke dimensions at no extra cost." },
    { title: "Free Installation", icon: "Wrench", desc: "Doorstep delivery and assembly included." },
  ],
  "Real Estate": [
    { title: "RERA Verified", icon: "ShieldCheck", desc: "Every listing checked against title & approvals." },
    { title: "Zero Brokerage Visits", icon: "BadgePercent", desc: "First 3 visits absolutely free." },
    { title: "Home Loan Assist", icon: "HandCoins", desc: "Tie-ups with 12 leading banks." },
    { title: "Verified Owners", icon: "UserCheck", desc: "Direct from owners, no middlemen." },
  ],
  Legal: [
    { title: "Senior Partners", icon: "Scale", desc: "Personally heard by senior advocates." },
    { title: "27+ Years Practice", icon: "Award", desc: "From High Court to Supreme Court." },
    { title: "Confidential", icon: "ShieldCheck", desc: "Strictly enforced NDAs and case privacy." },
    { title: "Transparent Billing", icon: "ReceiptText", desc: "Clear retainers, no hidden fees." },
  ],
  Automotive: [
    { title: "Authorised Partner", icon: "Award", desc: "XPEL · STEK · LLumar certified studio." },
    { title: "10-year Warranty", icon: "ShieldCheck", desc: "Industry-leading paint & coating warranty." },
    { title: "Climate-Controlled Bay", icon: "Sparkles", desc: "Dust-free environment for flawless finish." },
    { title: "Trained Master Detailers", icon: "Wrench", desc: "Each detailer 5+ years of experience." },
  ],
  "SaaS / Marketing": [
    { title: "Built for India", icon: "MapPin", desc: "WhatsApp BSP · DPDP-ready · Mumbai data residency." },
    { title: "AI-Powered", icon: "Sparkles", desc: "Smart segmentation, copy and send-time AI." },
    { title: "Best Deliverability", icon: "Send", desc: "99% inbox rate, dedicated IP available." },
    { title: "Real Support", icon: "Headphones", desc: "Human support — not bots — Mon–Sat." },
  ],
};

const WHY_DEFAULT = [
  { title: "Trusted Service", icon: "ShieldCheck", desc: "Years of consistent quality and satisfied customers." },
  { title: "Affordable Pricing", icon: "BadgePercent", desc: "Premium quality at honest, transparent prices." },
  { title: "Personalised Care", icon: "HeartHandshake", desc: "Every customer treated like family." },
  { title: "On-time Delivery", icon: "Clock", desc: "We respect your time as much as ours." },
];

export const WhyChooseUs = ({ template, dataTestid = "section-why-choose" }) => {
  const items = WHY_BY_INDUSTRY[template.industry] || WHY_DEFAULT;
  return (
    <SectionShell
      title={`Why choose ${template.brand.name.split(" ")[0]}?`}
      subtitle={`Reasons our customers stay, refer and come back.`}
      eyebrow="Why us"
      dataTestid={dataTestid}
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((it, i) => {
          const Cmp = Icons[it.icon] || Icons.Sparkles;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              data-testid={`why-card-${i}`}
              className="rounded-2xl p-6 border hover:-translate-y-1 transition-all"
              style={{
                backgroundColor: "var(--tpl-surface)",
                borderColor: "color-mix(in oklab, var(--tpl-text) 10%, transparent)",
              }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                style={{
                  background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 18%, transparent), color-mix(in oklab, var(--tpl-accent) 14%, transparent))`,
                  color: "var(--tpl-primary)",
                }}
              >
                <Cmp className="w-5 h-5" />
              </div>
              <h4 className="font-display font-bold text-base leading-snug">{it.title}</h4>
              <p className="mt-2 text-sm opacity-75 leading-relaxed">{it.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </SectionShell>
  );
};

// ─── MODULE BANNER (inner-page hero strip) ────────────────────────────────
export const ModuleBanner = ({ template, navItem, module }) => {
  if (!navItem) return null;
  const bg = module?.image || template.cover || template.hero?.image;
  return (
    <section className="relative" data-testid="module-banner">
      <div
        className="relative overflow-hidden"
        style={{
          background: bg
            ? undefined
            : `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 22%, var(--tpl-bg)), var(--tpl-bg))`,
        }}
      >
        {bg && (
          <>
            <img src={bg} alt={navItem.label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, color-mix(in oklab, var(--tpl-primary) 75%, transparent) 0%, color-mix(in oklab, var(--tpl-primary) 35%, transparent) 60%, color-mix(in oklab, #000 35%, transparent) 100%)`,
              }}
            />
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 lg:py-28 text-white">
          <nav className="text-xs flex items-center gap-2 opacity-90" data-testid="module-banner-breadcrumb">
            <Link to="/services" className="hover:underline">All Templates</Link>
            <span>›</span>
            <Link to={`/templates/${template.key}`} className="hover:underline">{template.brand.name}</Link>
            <span>›</span>
            <span className="opacity-90">{navItem.label}</span>
          </nav>
          <p className="mt-5 text-xs uppercase tracking-[0.24em] opacity-90 font-semibold">{template.industry} · {template.brand.name}</p>
          <h1 className="mt-2 font-display text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl">
            {module?.title || navItem.label}
          </h1>
          {module?.subtitle && (
            <p className="mt-4 text-base sm:text-lg opacity-90 max-w-2xl">{module.subtitle}</p>
          )}
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to={`/templates/${template.key}/${template.nav.find((n) => n.type === "contact")?.slug || "contact"}`}
              className="px-5 py-2.5 rounded-full font-semibold text-sm bg-white"
              style={{ color: "var(--tpl-primary)" }}
              data-testid="module-banner-cta"
            >
              Contact / Enquire
            </Link>
            <Link
              to={`/templates/${template.key}`}
              className="px-5 py-2.5 rounded-full font-semibold text-sm bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white"
              data-testid="module-banner-home"
            >
              ← Back to {template.brand.name.split(" ")[0]} home
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── BOTTOM CTA BAND ──────────────────────────────────────────────────────
export const BottomCTA = ({ template }) => {
  const contactSlug = template.nav.find((n) => n.type === "contact")?.slug || "contact";
  return (
    <section className="py-16 sm:py-20" data-testid="section-bottom-cta">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div
          className="rounded-3xl p-8 sm:p-12 text-center"
          style={{
            background: `linear-gradient(135deg, var(--tpl-primary), var(--tpl-accent))`,
            color: "#ffffff",
          }}
        >
          <p className="text-xs uppercase tracking-[0.24em] font-semibold opacity-90">Ready when you are</p>
          <h3 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold">
            Have a question? Let&apos;s talk.
          </h3>
          <p className="mt-3 text-sm sm:text-base opacity-90 max-w-xl mx-auto">
            Reach our team on WhatsApp, email, or fill the enquiry form — we respond within hours.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to={`/templates/${template.key}/${contactSlug}`}
              className="px-6 py-3 rounded-full font-semibold text-sm bg-white"
              style={{ color: "var(--tpl-primary)" }}
              data-testid="bottom-cta-contact"
            >
              Contact us
            </Link>
            <Link
              to={`/templates/${template.key}`}
              className="px-6 py-3 rounded-full font-semibold text-sm bg-white/15 border border-white/30 text-white hover:bg-white/25"
              data-testid="bottom-cta-explore"
            >
              Explore more
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};


// ─── Module Renderer ───────────────────────────────────────────────────────
// Maps a module's `type` (or its slug for specialised renders) to a component.
export const renderModule = (module, slug, template) => {
  if (!module) return null;
  const type = module.type;
  // Some module slugs map to a richer component
  if (slug === "departments") return <ItemGrid key={slug} data={module} variant="feature" dataTestid={`section-${slug}`} />;
  if (slug === "doctors" || slug === "trainers") return <People key={slug} data={module} dataTestid={`section-${slug}`} />;
  if (slug === "pricing") return <Pricing key={slug} data={module} dataTestid={`section-${slug}`} />;
  if (slug === "faq") return <Faq key={slug} data={module} dataTestid={`section-${slug}`} />;
  if (slug === "rooms") return <ItemGrid key={slug} data={module} variant="product" dataTestid={`section-${slug}`} />;
  if (slug === "properties") return <ItemGrid key={slug} data={module} variant="product" dataTestid={`section-${slug}`} />;
  if (slug === "packages") return <ItemGrid key={slug} data={module} variant="package" dataTestid={`section-${slug}`} />;
  if (slug === "collections") return <ItemGrid key={slug} data={module} variant="product" dataTestid={`section-${slug}`} />;
  if (slug === "products" || slug === "menu") return <ItemGrid key={slug} data={module} variant="product" dataTestid={`section-${slug}`} />;
  if (slug === "academics" || slug === "facilities" || slug === "features" || slug === "services") return <ItemGrid key={slug} data={module} variant="feature" dataTestid={`section-${slug}`} />;
  if (slug === "testimonials" || slug === "reviews") return <Reviews key={slug} data={module} dataTestid={`section-${slug}`} />;
  if (slug === "lookbook" || slug === "portfolio" || slug === "photo-view" || slug === "gallery") return <Gallery key={slug} data={module} dataTestid={`section-${slug}`} />;
  if (type === "contact") return <Contact key={slug} data={module} template={template} dataTestid={`section-${slug}`} />;
  if (type === "about") return <About key={slug} data={module} dataTestid={`section-${slug}`} />;
  return null;
};

// Build a friendly module accessor from the template (handles dashed slugs)
export const useTemplateModule = (template, slug) => useMemo(() => {
  if (!template || !slug) return null;
  const direct = template.modules[slug];
  if (direct) return { slug, ...direct, type: template.nav.find(n => n.slug === slug)?.type };
  return null;
}, [template, slug]);
